import { deleteApp, getApps, initializeApp } from "firebase/app";
import { Capacitor } from "@capacitor/core";
import { nativeMigrationConfigs, restoreSavedNativeSession } from "./nativeSessionRestore.js";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  sendEmailVerification,
  getAuth,
  indexedDBLocalPersistence,
  inMemoryPersistence,
  initializeAuth,
  isSignInWithEmailLink,
  linkWithCredential,
  onIdTokenChanged,
  reload,
  sendSignInLinkToEmail,
  sendPasswordResetEmail,
  signInAnonymously as firebaseSignInAnonymously,
  signInWithCustomToken as firebaseSignInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signOut,
} from "firebase/auth";

let authInstance;
let nativeSessionReady;
const nativeMigrationEnabled = import.meta.env.VITE_AXON_NATIVE_SESSION_MIGRATION === "true" &&
  Capacitor.getPlatform() === "android";

function productionConfig() {
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };
}

function previousNativeConfigs() {
  return nativeMigrationConfigs(productionConfig(), import.meta.env.VITE_AXON_LEGACY_FIREBASE_CONFIG);
}

function persistentAuth(app) {
  try {
    return initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence,
        browserSessionPersistence, inMemoryPersistence],
    });
  } catch {
    return getAuth(app);
  }
}

async function openPreviousNativeAuth(config) {
  const existing = getApps().find((app) => app.name === "[DEFAULT]");
  if (existing) await deleteApp(existing);
  const app = initializeApp(config);
  return { app, auth: persistentAuth(app) };
}

function readyAuth() {
  const primary = configuredAuth();
  if (!nativeMigrationEnabled) return Promise.resolve(primary);
  nativeSessionReady ||= restoreSavedNativeSession({
    primary,
    configs: previousNativeConfigs(),
    openPrevious: openPreviousNativeAuth,
    closePrevious: ({ app }) => deleteApp(app),
    accept: (token) => firebaseSignInWithCustomToken(primary, token),
    discardWrong: () => signOut(primary),
  }).then(() => primary);
  return nativeSessionReady;
}

// 16.09.2026 — ЗДЕСЬ БЫЛ ФОЛБЭК НА СТАРЫЙ ПРОЕКТ, И ОН БЫЛ ОПАСЕН.
// Константа AXON_FIREBASE_DEFAULTS держала боевые apiKey/authDomain/projectId/appId
// проекта `axonleads-app`, а конфиг читался как `env || DEFAULTS`. Проверка на
// незаполненные поля не срабатывала никогда: дефолты заполняли все четыре.
// При этом в `.env.production` Firebase-переменных не было вовсе — то есть прод
// собирался ИМЕННО по дефолтам.
// Последствие на переносе проекта: сборка с забытой переменной молча ушла бы в
// старый проект, и заметили бы это не по ошибке сборки, а по тому, что люди
// «почему-то ещё логинятся». Теперь конфиг обязателен и задаётся в .env.*,
// а отсутствие любого поля роняет сборку сразу и громко.
function configuredAuth() {
  if (authInstance) return authInstance;
  const config = productionConfig();
  const missing = Object.entries(config)
    .filter(([, value]) => !value)
    .map(([key]) => key);
  if (missing.length > 0) {
    throw new Error(`Firebase session configuration is incomplete: ${missing.join(", ")}`);
  }
  const name = nativeMigrationEnabled ? "axonleads-prod-native" : "[DEFAULT]";
  const app = getApps().find((item) => item.name === name) || initializeApp(config, name);
  // getAuth() тянет резолвер попапов, а тот грузит apis.google.com/js/api.js.
  // Наш CSP этот скрипт не пускает, и SDK молча зависал: кнопка «Смотреть
  // заказы» не делала НИЧЕГО — ни входа, ни ошибки. Проверено на проде 17.09.
  // Попапы и редиректы здесь не используются вовсе (вход гостем, ссылка на
  // почту и привязка идут обычными запросами), поэтому резолвер не нужен.
  // Так CSP остаётся узким, а не расширяется под чужой скрипт.
  authInstance = persistentAuth(app);
  authInstance.languageCode = "ru";
  return authInstance;
}

function emailLinkSettings(intent = "login") {
  const configuredLinkDomain = import.meta.env.VITE_FIREBASE_AUTH_LINK_DOMAIN;
  const configuredAppOrigin = import.meta.env.VITE_AXON_APP_ORIGIN || "https://app.axonleads.ru";
  const finishUrl = new URL("/auth/finish", configuredAppOrigin);
  if (intent === "link") finishUrl.searchParams.set("intent", "link");
  return {
    url: finishUrl.toString(),
    handleCodeInApp: true,
    linkDomain: configuredLinkDomain || "app.axonleads.ru",
  };
}

function publicFirebaseError(error, phase) {
  const code = typeof error?.code === "string" ? error.code : "";
  const messages = {
    "auth/invalid-email": "Проверьте email и попробуйте ещё раз.",
    "auth/network-request-failed": "Нет связи. Проверьте интернет и попробуйте ещё раз.",
    "auth/too-many-requests": "Слишком много попыток. Подождите немного и попробуйте снова.",
    "auth/quota-exceeded": "Отправка ссылок временно недоступна. Попробуйте позже.",
    "auth/expired-action-code": "Ссылка устарела. Получите новую.",
    "auth/invalid-action-code": "Ссылка недействительна или уже использована.",
    "auth/invalid-credential": "Не получилось войти. Проверьте данные доступа.",
    "auth/user-not-found": "Не получилось войти. Проверьте данные доступа.",
    "auth/wrong-password": "Не получилось войти. Проверьте данные доступа.",
    // Так Firebase отвечает, когда анонимный вход выключен в настройках проекта.
    // Человеку это ни о чём не говорит, поэтому пишем по-человечески.
    // Почта уже принадлежит другому аккаунту. Firebase справедливо
    // отказывается привязать её к гостю. Раньше человек видел здесь
    // «откройте письмо в том же браузере» — совет, который ничего не чинит.
    "auth/email-already-in-use": "Эта почта уже привязана к другому аккаунту AxonLeads. Текущие данные не перенесены — не выходите, пока не решите, как сохранить их.",
    "auth/weak-password": "Пароль слишком простой. Нужно не меньше 6 символов.",
    "auth/missing-password": "Введите пароль.",
    "auth/invalid-login-credentials": "Не получилось войти. Проверьте почту и пароль.",
    "auth/credential-already-in-use": "Эта почта уже привязана к другому аккаунту AxonLeads. Текущие данные не перенесены — не выходите, пока не решите, как сохранить их.",
    "auth/admin-restricted-operation": "Гостевой вход сейчас недоступен. Войдите по ссылке на почту.",
    "auth/operation-not-allowed": "Гостевой вход сейчас недоступен. Войдите по ссылке на почту.",
  };
  // 16.09 — у гостевого входа свой фолбэк: предлагать «получите новую ссылку»
  // человеку, который никакой ссылки не запрашивал, — сбивать с толку на ровном месте.
  const fallback = phase === "send"
    ? "Не получилось отправить ссылку. Попробуйте ещё раз."
    : phase === "guest"
    ? "Не получилось начать. Попробуйте ещё раз."
    : phase === "register"
    ? "Не получилось создать аккаунт. Попробуйте ещё раз."
    : phase === "reset"
    ? "Не получилось отправить письмо для восстановления. Попробуйте позже."
    : phase === "verify"
    ? "Не получилось проверить почту. Попробуйте ещё раз."
    : "Не получилось завершить вход. Получите новую ссылку.";
  return new Error(messages[code] || fallback);
}

export const firebaseSessionClient = {
  subscribe(listener) {
    try {
      if (nativeMigrationEnabled) {
        let cancelled = false;
        let unsubscribe;
        readyAuth().then((auth) => {
          if (!cancelled) unsubscribe = onIdTokenChanged(
            auth, (user) => listener(user, null), (error) => listener(null, error),
          );
        }).catch((error) => { if (!cancelled) listener(null, error); });
        return () => { cancelled = true; unsubscribe?.(); };
      }
      return onIdTokenChanged(
        configuredAuth(),
        (user) => listener(user, null),
        (error) => listener(null, error),
      );
    } catch (error) {
      queueMicrotask(() => listener(null, error));
      return () => {};
    }
  },

  async sendEmailLink(email, intent = "login") {
    try {
      return await sendSignInLinkToEmail((await readyAuth()), email, emailLinkSettings(intent));
    } catch (error) {
      throw publicFirebaseError(error, "send");
    }
  },

  isEmailLink(url) {
    return isSignInWithEmailLink(configuredAuth(), url);
  },

  async finishEmailLink(email, url) {
    try {
      return await signInWithEmailLink((await readyAuth()), email, url);
    } catch (error) {
      throw publicFirebaseError(error, "finish");
    }
  },

  async linkEmail(email, url) {
    try {
      const auth = (await readyAuth());
      if (!auth.currentUser) throw new Error("missing-current-user");
      const credential = EmailAuthProvider.credentialWithLink(email, url);
      return await linkWithCredential(auth.currentUser, credential);
    } catch (error) {
      throw publicFirebaseError(error, "finish");
    }
  },

  // Гостевой вход. Ни одного поля: человек работает сразу, почту предлагаем
  // позже — когда накопится то, что жалко потерять.
  // Важно: Firebase при последующей привязке почты СОХРАНЯЕТ этот же uid,
  // поэтому гость не теряет историю, превращаясь в постоянного пользователя.
  // Регистрация почтой и паролем. Привычный путь: человек решил платить —
  // ему нужен аккаунт, в который он вернётся с любого устройства.
  // Работает без нашей почтовой отправки, в отличие от ссылки и кода.
  async registerWithPassword(email, password) {
    try {
      const credential = await createUserWithEmailAndPassword((await readyAuth()), email, password);
      // Письмо-подтверждение шлём сразу, но вход им НЕ загораживаем: человек
      // работает, а адрес подтверждается своим чередом. Молчаливый отказ
      // здесь недопустим — но и повод отменить регистрацию из него никакой.
      await sendEmailVerification(credential.user).catch(() => undefined);
      return credential;
    } catch (error) {
      throw publicFirebaseError(error, "register");
    }
  },

  // Повторно отправить подтверждение адреса.
  async resendEmailVerification() {
    const auth = (await readyAuth());
    if (!auth.currentUser) throw new Error("Сессия завершилась. Войдите ещё раз.");
    try {
      await sendEmailVerification(auth.currentUser);
    } catch (error) {
      throw publicFirebaseError(error, "verify");
    }
  },

  async refreshEmailVerification() {
    const auth = (await readyAuth());
    if (!auth.currentUser) throw new Error("Сессия завершилась. Войдите ещё раз.");
    try {
      await reload(auth.currentUser);
      await auth.currentUser.getIdToken(true);
      return {
        uid: auth.currentUser.uid,
        email: auth.currentUser.email,
        emailVerified: auth.currentUser.emailVerified === true,
      };
    } catch (error) {
      throw publicFirebaseError(error, "verify");
    }
  },

  async sendPasswordReset(email) {
    try {
      const url = new URL("/login", import.meta.env.VITE_AXON_APP_ORIGIN || "https://app.axonleads.ru");
      await sendPasswordResetEmail((await readyAuth()), email, { url: url.toString() });
    } catch (error) {
      if (error?.code === "auth/user-not-found") return;
      throw publicFirebaseError(error, "reset");
    }
  },

  // Гость задаёт пароль и перестаёт быть гостем. Firebase СОХРАНЯЕТ тот же
  // uid, поэтому лиды, визитка и история остаются его — переносить нечего.
  async linkPassword(email, password) {
    try {
      const auth = (await readyAuth());
      if (!auth.currentUser) throw new Error("missing-current-user");
      const uid = auth.currentUser.uid;
      const credential = EmailAuthProvider.credential(email, password);
      const linked = await linkWithCredential(auth.currentUser, credential);
      if (linked.user.uid !== uid) throw new Error("Аккаунт изменился. Не выходите и обратитесь в поддержку.");
      let verificationSent = true;
      try {
        await sendEmailVerification(linked.user);
      } catch {
        verificationSent = false;
      }
      return { ...linked, verificationSent };
    } catch (error) {
      if (error instanceof Error && !error.code) throw error;
      throw publicFirebaseError(error, "register");
    }
  },

  async signInAsGuest() {
    try {
      return await firebaseSignInAnonymously((await readyAuth()));
    } catch (error) {
      throw publicFirebaseError(error, "guest");
    }
  },

  async signInWithCustomToken(customToken) {
    try {
      return await firebaseSignInWithCustomToken((await readyAuth()), customToken);
    } catch (error) {
      throw publicFirebaseError(error, "finish");
    }
  },

  async signInWithPassword(email, password) {
    try {
      return await signInWithEmailAndPassword((await readyAuth()), email, password);
    } catch (error) {
      throw publicFirebaseError(error, "finish");
    }
  },

  async signOut() {
    const auth = await readyAuth();
    if (nativeMigrationEnabled) {
      // Explicit logout also clears preserved old SDK sessions, so reopening
      // the app cannot log the user back into an account they just left.
      for (const config of previousNativeConfigs()) {
        const previous = await openPreviousNativeAuth(config);
        try {
          await previous.auth.authStateReady();
          await signOut(previous.auth);
        } finally {
          await deleteApp(previous.app);
        }
      }
    }
    return signOut(auth);
  },

  async getIdToken(forceRefresh = false) {
    try {
      const user = (await readyAuth()).currentUser;
      return user ? await user.getIdToken(forceRefresh) : null;
    } catch (error) {
      throw publicFirebaseError(error, "finish");
    }
  },
};
